import { Alert, App, Form, Spin } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomInput,
  CustomModal,
  CustomSelect,
} from "sipli-fleet-core/components";
import {
  useMutationUpdateCommand,
  useQueryGetCommandById,
  useQueryGetCommandLookup,
  useMutationSendCommand,
} from "src/queries/commands.queries";
import { useCommandHistoryEvents } from "src/hooks/useCommandHistoryEvents";
import type {
  CommandSchedulerStatus,
  CommandStatus,
  SendCommandPayload,
} from "src/services/commandService";
import type { CommandHistoryRecord } from "src/services/commandHistoryService";

export interface CommandSettingsFormValues {
  commandType: number;
  expirationHours: number;
}

export interface SendCommandFormValues {
  commandType: number;
  commandForms: Record<string, string>;
  schedulerStatus?: CommandSchedulerStatus;
}

interface CommandSettingsModalProps {
  open: boolean;
  modelId?: string | number;
  onClose: () => void;
  onSubmit?: (values: CommandSettingsFormValues) => void;
}

interface SendCommandModalProps {
  open: boolean;
  modelId?: string | number;
  vehicleId?: string | number;
  onClose: () => void;
  onCommandSent?: (record: CommandHistoryRecord) => void;
  onCommandStatusChange?: (historyId: number, status: CommandStatus) => void;
  onCommandComplete?: (historyId: number) => void;
  onSubmit?: (values: SendCommandFormValues) => void;
}

const getCommandFormField = (commandForm: string) =>
  commandForm.replace(/^'(.*?)'$/, "$1");

const getCommandFormLabel = (commandForm: string) => {
  const field = getCommandFormField(commandForm);
  return field.charAt(0).toUpperCase() + field.slice(1);
};

const MAX_COMMAND_EVENT_RECONNECT_ATTEMPTS = 5;

interface CommandRequestFeedbackProps {
  isLoading: boolean;
  isError: boolean;
}

const CommandRequestFeedback = ({
  isLoading,
  isError,
}: CommandRequestFeedbackProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray9">
        <Spin size="small" />
        <span>{t("loading_command_details")}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("command_detail_failed")}
        className="!py-1.5"
      />
    );
  }

  return null;
};

const CommandSettingsModal = ({
  open,
  modelId,
  onClose,
  onSubmit,
}: CommandSettingsModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<CommandSettingsFormValues>();
  const selectedCommandId = Form.useWatch("commandType", form);
  const expirationHours = Form.useWatch("expirationHours", form);
  const parsedExpirationHours = Number(expirationHours);
  const isSettingsFormComplete =
    !!selectedCommandId &&
    expirationHours !== undefined &&
    expirationHours !== null &&
    String(expirationHours).trim() !== "" &&
    Number.isFinite(parsedExpirationHours);
  const {
    data: commandLookup = [],
    isFetching: isCommandLookupLoading,
    isError: isCommandLookupError,
  } = useQueryGetCommandLookup(modelId, { enabled: open && !!modelId });
  const {
    data: commandDetail,
    isFetching: isCommandDetailLoading,
    isError: isCommandDetailError,
  } = useQueryGetCommandById(selectedCommandId, {
    enabled: open && !!selectedCommandId,
  });
  const { mutateAsync: updateCommand, isPending: isUpdatingCommand } =
    useMutationUpdateCommand();
  const commandTypeOptions = commandLookup.map((command) => ({
    label: command.internalName,
    value: command.id,
  }));

  useEffect(() => {
    if (!open || !commandDetail) return;

    form.setFieldValue("expirationHours", commandDetail.expire);
  }, [commandDetail, form, open]);

  useEffect(() => {
    if (open && isCommandLookupError) {
      message.error(t("command_lookup_failed"));
    }
  }, [isCommandLookupError, message, open, t]);

  useEffect(() => {
    if (open && selectedCommandId && isCommandDetailError) {
      message.error(t("command_detail_failed"));
    }
  }, [isCommandDetailError, message, open, selectedCommandId, t]);

  const closeModal = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async (values: CommandSettingsFormValues) => {
    try {
      await updateCommand({
        commandId: values.commandType,
        payload: {
          expire: Number(values.expirationHours),
        },
      });
      onSubmit?.(values);
      message.success(t("command_settings_saved"));
      closeModal();
    } catch {
      message.error(t("command_settings_save_failed"));
    }
  };

  return (
    <CustomModal
      title={
        <span className="text-[19.93px] font-bold leading-7">
          {t("command_settings")}
        </span>
      }
      open={open}
      centered
      width={497}
      onCancel={closeModal}
      onOk={() => form.submit()}
      cancelText={t("cancel")}
      okText={t("done")}
      okBtnLoading={isUpdatingCommand}
      styles={{
        content: {
          borderRadius: 16,
          padding: 24,
        },
        body: {
          padding: 0,
        },
      }}
      okButtonProps={{
        className: "!w-[84px] !h-10 !text-white",
        disabled:
          !isSettingsFormComplete ||
          isCommandDetailLoading ||
          isCommandDetailError,
      }}
      cancelButtonProps={{ className: "!w-[96px] !h-10" }}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          expirationHours: 0,
        }}
        onFinish={handleFinish}
        className="pt-6 [&_.ant-form-item-label>label]:!text-gray9"
      >
        <div className="flex items-center justify-between">
          <Form.Item
            name="commandType"
            label={t("command_type")}
            rules={[{ required: true, message: t("field_required") }]}
            className="!mb-0"
          >
            <CustomSelect
              options={commandTypeOptions}
              loading={isCommandLookupLoading}
              disabled={!modelId || isCommandLookupLoading}
              placeholder={t("select_command")}
              showSearch
              optionFilterProp="label"
              className="!h-8 !w-[251px] [&_.ant-select-selector]:!h-8 [&_.ant-select-selector]:!rounded-[4px] [&_.ant-select-selection-item]:!leading-[30px]"
            />
          </Form.Item>
          <Form.Item
            name="expirationHours"
            label={t("expiration_time_hours")}
            rules={[{ required: true, message: t("field_required") }]}
            className="!mb-0"
          >
            <CustomInput
              type="number"
              min={0}
              disabled={!selectedCommandId || isCommandDetailLoading}
              className="!mt-0 !w-[182px] !h-8 !rounded-[4px]"
            />
          </Form.Item>
        </div>
        {isCommandLookupError && (
          <Alert
            type="error"
            showIcon
            message={t("command_lookup_failed")}
            className="!mt-3 !py-1.5"
          />
        )}
        {!!selectedCommandId && !isCommandLookupError && (
          <div className="mt-3">
            <CommandRequestFeedback
              isLoading={isCommandDetailLoading}
              isError={isCommandDetailError}
            />
          </div>
        )}
      </Form>
    </CustomModal>
  );
};

const SendCommandModal = ({
  open,
  modelId,
  vehicleId,
  onClose,
  onCommandSent,
  onCommandStatusChange,
  onCommandComplete,
  onSubmit,
}: SendCommandModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<SendCommandFormValues>();
  const selectedCommandId = Form.useWatch("commandType", form);
  const commandFormValues = Form.useWatch("commandForms", form);
  const [historyId, setHistoryId] = useState<number>();
  const {
    data: commandLookup = [],
    isFetching: isCommandLookupLoading,
    isError: isCommandLookupError,
  } = useQueryGetCommandLookup(modelId, { enabled: open && !!modelId });
  const {
    data: commandDetail,
    isFetching: isCommandDetailLoading,
    isError: isCommandDetailError,
  } = useQueryGetCommandById(selectedCommandId, {
    enabled: open && !!selectedCommandId,
  });
  const { mutateAsync: sendCommand, isPending: isSendingCommand } =
    useMutationSendCommand();
  const handleCommandStatus = useCallback((commandHistoryId: number, status: CommandStatus) => {
    onCommandStatusChange?.(commandHistoryId, status);
    if (status !== 3 && status !== 4) return;

    if (status === 3) {
      message.success(t("command_completed_successfully"));
    } else {
      message.error(t("command_execution_failed"));
    }

    setHistoryId(undefined);
    onCommandComplete?.(commandHistoryId);
  }, [message, onCommandComplete, onCommandStatusChange, t]);
  const {
    error: commandEventError,
    reconnectAttempts: commandEventReconnectAttempts,
  } = useCommandHistoryEvents({
    historyId,
    maxReconnectAttempts: MAX_COMMAND_EVENT_RECONNECT_ATTEMPTS,
    onMessage: ({ status }) => {
      if (historyId) handleCommandStatus(historyId, status);
    },
  });
  const commandTypeOptions = commandLookup.map((command) => ({
    label: command.internalName,
    value: command.id,
  }));
  const commandFields = commandDetail?.commandForms ?? [];
  const areDynamicFieldsComplete = commandFields.every((commandForm) => {
    const fieldKey = getCommandFormField(commandForm);
    return String(commandFormValues?.[fieldKey] ?? "").trim().length > 0;
  });
  const isSendFormComplete =
    !!selectedCommandId &&
    areDynamicFieldsComplete &&
    !!vehicleId;

  useEffect(() => {
    form.setFieldValue("commandForms", {});
  }, [form, selectedCommandId]);

  useEffect(() => {
    if (open && isCommandLookupError) {
      message.error(t("command_lookup_failed"));
    }
  }, [isCommandLookupError, message, open, t]);

  useEffect(() => {
    if (open && selectedCommandId && isCommandDetailError) {
      message.error(t("command_detail_failed"));
    }
  }, [isCommandDetailError, message, open, selectedCommandId, t]);

  useEffect(() => {
    if (
      !historyId ||
      !commandEventError ||
      commandEventReconnectAttempts < MAX_COMMAND_EVENT_RECONNECT_ATTEMPTS
    ) {
      return;
    }

    message.error(t("command_execution_failed"));
    setHistoryId(undefined);
    onCommandComplete?.(historyId);
  }, [
    commandEventError,
    commandEventReconnectAttempts,
    historyId,
    message,
    onCommandComplete,
    t,
  ]);

  const closeModal = () => {
    form.resetFields();
    onClose();
  };

  const handleFinish = async (values: SendCommandFormValues) => {
    if (!vehicleId) return;

    try {
      const schedulerStatus = values.schedulerStatus;
      const isScheduled =
        schedulerStatus !== undefined &&
        schedulerStatus !== null;
      const payload: SendCommandPayload = isScheduled
        ? {
            commandId: values.commandType,
            params: values.commandForms ?? {},
            scheduledCommand: true,
            schedulerStatus,
            schedulerType: "BASE",
          }
        : {
            commandId: values.commandType,
            params: values.commandForms ?? {},
            scheduledCommand: false,
          };
      const result = await sendCommand({
        vehicleId,
        payload,
      });

      const commandExecution = result.data.response;
      onCommandSent?.(commandExecution);
      onSubmit?.(values);
      message.success(t("command_submitted"));
      closeModal();

      if (commandExecution.status === 3 || commandExecution.status === 4) {
        handleCommandStatus(commandExecution.id, commandExecution.status);
      } else if (commandExecution.id) {
        setHistoryId(commandExecution.id);
      }
    } catch {
      message.error(t("command_submit_failed"));
    }
  };

  return (
    <CustomModal
      title={
        <span className="text-[19.93px] font-bold leading-7">
          {t("send_command")}
        </span>
      }
      open={open}
      centered
      width={497}
      onCancel={closeModal}
      onOk={() => form.submit()}
      cancelText={t("cancel")}
      okText={t("done")}
      okBtnLoading={isSendingCommand}
      styles={{
        content: {
          borderRadius: 16,
          padding: 24,
        },
        body: {
          padding: 0,
        },
      }}
      okButtonProps={{
        className: "!w-[84px] !h-10 !text-white",
        disabled:
          !isSendFormComplete ||
          isCommandDetailLoading ||
          isCommandDetailError ||
          isSendingCommand,
      }}
      cancelButtonProps={{ className: "!w-[96px] !h-10" }}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          commandForms: {},
        }}
        onFinish={handleFinish}
        className="flex flex-col gap-4 pt-6 [&_.ant-form-item-label>label]:!text-gray9"
      >
        <Form.Item
          name="commandType"
          label={t("command_type")}
          rules={[{ required: true, message: t("field_required") }]}
          className="!mb-0"
        >
          <CustomSelect
            options={commandTypeOptions}
            loading={isCommandLookupLoading}
            disabled={!modelId || isCommandLookupLoading}
            placeholder={t("select_command")}
            showSearch
            optionFilterProp="label"
            className="!h-8 [&_.ant-select-selector]:!h-8 [&_.ant-select-selector]:!rounded-[4px] [&_.ant-select-selection-item]:!leading-[30px]"
          />
        </Form.Item>
        {isCommandLookupError && (
          <Alert
            type="error"
            showIcon
            message={t("command_lookup_failed")}
            className="!py-1.5"
          />
        )}
        {!!selectedCommandId && !isCommandLookupError && (
          <CommandRequestFeedback
            isLoading={isCommandDetailLoading}
            isError={isCommandDetailError}
          />
        )}
        {commandFields.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            {commandFields.map((commandForm, index) => {
              const fieldKey = getCommandFormField(commandForm);
              const isOddLastField =
                commandFields.length % 2 === 1 &&
                index === commandFields.length - 1;

              return (
                <Form.Item
                  key={fieldKey}
                  name={["commandForms", fieldKey]}
                  label={getCommandFormLabel(commandForm)}
                  rules={[{ required: true, message: t("field_required") }]}
                  className={isOddLastField ? "!mb-0 col-span-2" : "!mb-0"}
                >
                  <CustomInput className="!mt-0 !h-8 !rounded-[4px]" />
                </Form.Item>
              );
            })}
          </div>
        )}
        <Form.Item
          name="schedulerStatus"
          label={t("scheduling")}
          className="!mb-0"
        >
          <CustomSelect
            allowClear
            placeholder={t("none")}
            className="!h-8 [&_.ant-select-selector]:!h-8 [&_.ant-select-selector]:!rounded-[4px] [&_.ant-select-selection-item]:!leading-[30px]"
            options={[
              { label: t("when_vehicle_off"), value: 0 },
              { label: t("when_vehicle_on"), value: 1 },
              {
                label: t("when_vehicle_available"),
                value: 2,
              },
            ]}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export { CommandSettingsModal, SendCommandModal };
